import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "../../../interceptor/axios-config";
import Swal from "sweetalert2";
import { useGrade } from "../../../context/GradeContext";
import config from "../../../config";
import statesWithDistricts from "../../../data/data";
import { getCurrentSession } from "../../../utils/session";

const initialFormData = {
  full_name: "",
  nationality: "Indian",
  religion: "Christian",
  pen: "",
  dob: "",
  pass_photo: null,
  class_admitted: "",
  roll_no: "",
  sex: "",
  email: "",
  contact_no: "",
  place_of_birth: "",
  admission_status: "success",
  father_name: "",
  mother_name: "",
  guardian_name: "",
  aadhar_no: "",
  r_address: "",
  state: "",
  district: "",
  city: "",
  pincode: "",
};

const AdmissionModal = forwardRef((props, ref) => {
  const { grade } = useGrade();

  const [gradeId, setGradeId] = useState("");
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");

  const [templateError, setTemplateError] = useState(false);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const session = getCurrentSession();

  const [feeStructure, setFeeStructure] = useState(null);
  const [totalFee, setTotalFee] = useState(0);

  // ===============================
// RESET PROPERLY ON OPEN
// ===============================
useImperativeHandle(ref, () => ({
  openModal() {
    setFormData(initialFormData);
    setFeeStructure(null);
    setTotalFee(0);
    setTemplateError(false);
    setGradeId("");
    setSelectedSection("");
    setAvailableSections([]);
    setShow(true);
  },
}));

const closeModal = () => {
  setShow(false);
  setFormData(initialFormData);
  setFeeStructure(null);
  setTotalFee(0);
  setTemplateError(false);
  setGradeId("");
  setSelectedSection("");
  setAvailableSections([]);
};

// ===============================
// GRADE CHANGE
// ===============================
const handleGradeChange = (e) => {
  const selectedGrade = e.target.value;

  setGradeId(selectedGrade);

  if (!selectedGrade) {
    setAvailableSections([]);
    setSelectedSection("");
    setFormData(prev => ({ ...prev, class_admitted: "" }));
    return;
  }

  const sections = [
    ...new Set(
      grade
        .filter(g => String(g.grade_id) === String(selectedGrade))
        .map(g => g.section_name)
    )
  ];

  setAvailableSections(sections);

  if (sections.length === 1) {
    // 🔥 pass grade explicitly to avoid stale state
    handleSectionSelect(sections[0], selectedGrade);
  } else {
    setSelectedSection("");
    setFormData(prev => ({ ...prev, class_admitted: "" }));
  }
};

// ===============================
// SECTION SELECT
// ===============================
const handleSectionSelect = (section, overrideGradeId = null) => {
  const gId = overrideGradeId ?? gradeId;

  setSelectedSection(section);

  const selectedClass = grade.find(
    g =>
      String(g.grade_id) === String(gId) &&
      g.section_name === section
  );

  if (!selectedClass) {
    console.warn("Class not found for:", gId, section);
    return;
  }

  setFormData(prev => ({
    ...prev,
    class_admitted: selectedClass.value
  }));
};

// ===============================
// INPUT CHANGE
// ===============================
const handleChange = (e) => {
  const { name, value, type } = e.target;

  if (type === "file") {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      pass_photo: file,          // ✅ actual file for API
      preview_photo: URL.createObjectURL(file) // ✅ preview only
    }));
  } else {
    setFormData(prev => ({ ...prev, [name]: value }));
  }
};



// ===============================
// LOAD FEES (STABLE)
// ===============================
useEffect(() => {
  if (!formData.class_admitted) {
    setFeeStructure(null);
    setTotalFee(0);
    setTemplateError(false);
    return;
  }

  let isActive = true; // 🔥 prevent stale updates

  const timeout = setTimeout(async () => {
    try {
      const res = await axios.get(
        `${config.apiBaseUrl}/fees/get-fee-structure.php`,
        {
          params: {
            class_id: formData.class_admitted,
            session: session
          }
        }
      );

      if (!isActive) return;

      if (res.data.success && res.data.data?.length > 0) {
        setFeeStructure(res.data.data);
        setTotalFee(Number(res.data.total));
        setTemplateError(false);
      } else {
        setFeeStructure(null);
        setTotalFee(0);
        setTemplateError(true);
      }

    } catch (err) {
      if (!isActive) return;
      console.error(err);
      setTemplateError(true);
    }
  }, 200);

  return () => {
    isActive = false;
    clearTimeout(timeout);
  };

}, [formData.class_admitted, session]);

// ===============================
// SUBMIT
// ===============================
const handleSubmit = async () => {
    if (loading) return;

    try {
      setLoading(true);

      const fd = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "preview_photo") {
          fd.append(key, formData[key]);
        }
      });

      const res = await axios.post(
        `${config.apiBaseUrl}/students/admit-student.php`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        await Swal.fire("Success", "Student admitted successfully", "success");

        setShowPreview(false);
        closeModal();

        props.onSuccess?.(res.data.student);

      } else {
        await Swal.fire("Error", res.data.error || "Admission failed", "error");
      }

    } catch (err) {
      console.error(err);
      await Swal.fire("Error", "Server error", "error");
    } finally {
      setLoading(false);
    }
  };

const handleStateChange = (e) => {
  const selectedState = e.target.value;

  setFormData(prev => ({
    ...prev,
    state: selectedState,
    district: "" // reset district when state changes
  }));
};

  return (
    <>
      <Modal show={show} onHide={closeModal} size="xl" centered>
    <Modal.Header closeButton>
      <Modal.Title>Student Admission</Modal.Title>
    </Modal.Header>

    <Modal.Body>

      {/* 🔥 SHOW ERROR ONLY WHEN CLASS SELECTED */}
      {templateError && formData.class_admitted && (
        <div className="alert alert-danger">
          Fee template not found. Please create fee template first.
        </div>
      )}

      <Form>
        <Row>

          {/* LEFT COLUMN */}
          <Col md={6}>
            <div className="d-flex align-items-start gap-4 flex-wrap mb-3">

              {/* CLASS DROPDOWN */}
              <div style={{ minWidth: 300 }}>
                <Form.Label>Class</Form.Label>
                <Form.Select
                  value={gradeId}
                  onChange={handleGradeChange}
                >
                  <option value="">Select Class</option>

                  {[...new Map(
                    grade.map(g => [g.grade_id, g])
                  ).values()].map(g => (
                    <option key={g.grade_id} value={g.grade_id}>
                      {/* Class-{g.grade_name} */}
                      {g.grade_name}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* SECTION */}
              {gradeId && (
                <div>
                  <Form.Label>Section</Form.Label>

                  <div className="d-flex gap-3 flex-wrap">
                    {availableSections.map(sec => (
                      <Form.Check
                        key={sec}
                        type="radio"
                        label={sec}
                        name="section"
                        checked={selectedSection === sec}
                        onChange={() => handleSectionSelect(sec)}
                      />
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* 🔥 FEE STATUS */}
            {formData.class_admitted && !templateError && !feeStructure && (
              <div className="alert alert-warning">
                Loading fee structure...
              </div>
            )}

            {feeStructure && (
              <div className="alert alert-info">
                <strong>Total Fees:</strong> ₹{totalFee.toFixed(2)}
              </div>
            )}

            {/* FORM FIELDS */}
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Roll No</Form.Label>
              <Form.Control
                name="roll_no"
                value={formData.roll_no}
                onChange={handleChange}
                placeholder="Enter roll number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nationality</Form.Label>
              <Form.Control name="nationality" value={formData.nationality} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Religion</Form.Label>
              <Form.Select name="religion" value={formData.religion} onChange={handleChange}>
                <option>Christian</option>
                <option>Hindu</option>
                <option>Muslim</option>
                <option>Other</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Father Name</Form.Label>
              <Form.Control name="father_name" value={formData.father_name} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mother Name</Form.Label>
              <Form.Control name="mother_name" value={formData.mother_name} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Guardian Name</Form.Label>
              <Form.Control name="guardian_name" value={formData.guardian_name} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DOB</Form.Label>
              <Form.Control type="date" name="dob" value={formData.dob} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Place of Birth</Form.Label>
              <Form.Control name="place_of_birth" value={formData.place_of_birth} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Gender</Form.Label>
              <Form.Select name="sex" value={formData.sex} onChange={handleChange}>
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
              </Form.Select>
            </Form.Group>

          </Col>

          {/* RIGHT COLUMN */}
          <Col md={6}>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control name="email" value={formData.email} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contact Number</Form.Label>
              <Form.Control name="contact_no" value={formData.contact_no} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>PEN</Form.Label>
              <Form.Control name="pen" value={formData.pen} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Aadhar</Form.Label>
              <Form.Control name="aadhar_no" value={formData.aadhar_no} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control name="r_address" value={formData.r_address} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Select name="state" value={formData.state} onChange={handleStateChange}>
                <option value="">Select State</option>
                {statesWithDistricts.map(s => (
                  <option key={s.name}>{s.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>District</Form.Label>
              <Form.Select
                name="district"
                value={formData.district}
                onChange={handleChange}
                disabled={!formData.state}
              >
                <option value="">Select District</option>
                {statesWithDistricts
                  .find(s => s.name === formData.state)
                  ?.districts.map(d => (
                    <option key={d.name}>{d.name}</option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control name="city" value={formData.city} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pincode</Form.Label>
              <Form.Control name="pincode" value={formData.pincode} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Photo</Form.Label>
              <Form.Control type="file" name="pass_photo" onChange={handleChange} />
            </Form.Group>

          </Col>

        </Row>
      </Form>
    </Modal.Body>

    <Modal.Footer>
      {/* 🔥 DISABLE UNTIL READY */}
      <Button
        variant="info"
        disabled={!formData.class_admitted || templateError}
        onClick={() => setShowPreview(true)}
      >
        Preview
      </Button>

      <Button variant="secondary" onClick={closeModal}>
        Cancel
      </Button>
    </Modal.Footer>
  </Modal>

      {/* PREVIEW MODAL */}

      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Student Preview</Modal.Title>
        </Modal.Header>

        <Modal.Body>

          {/* PHOTO ON TOP */}
          {formData.preview_photo && (
              <img
                src={formData.preview_photo}
                alt="Student"
                style={{ width: 140, borderRadius: 8 }}
              />
            )}

          <Row>

            {Object.entries(formData)
              .filter(([key]) => key !== "pass_photo") // remove photo from table
              .map(([key, val]) => {

                const displayValue =
                  key === "class_admitted"
                    ? grade.find(g => String(g.value) === String(val))?.label || val
                    : val;

                return (
                  <Col md={4} key={key} className="mb-2">
                    <strong style={{ textTransform: "capitalize" }}>
                      {key.replace(/_/g, " ")}:
                    </strong>
                    <div>{displayValue || "-"}</div>
                  </Col>
                );
              })}

          </Row>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Edit
          </Button>

          <Button
            variant="success"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? "Saving..." : "Confirm & Save"}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
});

export default AdmissionModal;
